import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Image as ImageIcon, Upload, X, Crop as CropIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BrandLogoUploader
 * -----------------
 * Lets the owner pick an image → crop it (freeform or square) → save as
 * business.logo_url. Cropped image is stored as a compressed data URL so it
 * appears immediately in every rendered document without needing our object
 * storage flow.
 */
export default function BrandLogoUploader({ businessId, currentUrl, onSaved }) {
  const inputRef = useRef(null);
  const [pickedSrc, setPickedSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(null); // null = free
  const [croppedArea, setCroppedArea] = useState(null);
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    const reader = new FileReader();
    reader.onload = () => setPickedSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const onCropComplete = useCallback((_, area) => setCroppedArea(area), []);

  const save = async () => {
    if (!pickedSrc || !croppedArea) return;
    setSaving(true);
    try {
      const dataUrl = await cropToDataUrl(pickedSrc, croppedArea);
      await api.put("/business", { logo_url: dataUrl });
      toast.success("Logo updated");
      setPickedSrc(null);
      onSaved?.(dataUrl);
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.put("/business", { logo_url: "" });
      toast.success("Logo removed");
      onSaved?.(null);
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6" data-testid="brand-logo-uploader">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold">Brand logo</h2>
          <p className="text-sm text-zinc-500 mt-1">Shown on estimates, invoices, and their PDF/print/email versions. Crop to focus on the mark itself.</p>
        </div>
        <div className="flex items-center gap-2">
          {currentUrl && (
            <Button variant="outline" size="sm" onClick={removeLogo} data-testid="logo-remove-btn">
              <X className="w-4 h-4 mr-1"/> Remove
            </Button>
          )}
          <Button onClick={() => inputRef.current?.click()} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="logo-pick-btn">
            <Upload className="w-4 h-4 mr-1"/> {currentUrl ? "Replace" : "Upload"}
          </Button>
          <input type="file" ref={inputRef} accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input"/>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-2">
        <div className="w-28 h-28 bg-zinc-50 border border-zinc-200 rounded-lg flex items-center justify-center overflow-hidden">
          {currentUrl
            ? <img src={currentUrl} alt="logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview-img"/>
            : <ImageIcon className="w-8 h-8 text-zinc-300"/>}
        </div>
        <div className="text-xs text-zinc-500">
          {currentUrl ? "Current logo. Upload a new file to replace it — you'll be able to crop before saving." : "No logo set yet."}
          <div className="text-[11px] text-zinc-400 mt-1">PNG or JPG. Transparent backgrounds work best.</div>
        </div>
      </div>

      {/* Crop modal */}
      <Dialog open={!!pickedSrc} onOpenChange={(v) => !v && setPickedSrc(null)}>
        <DialogContent className="max-w-3xl" data-testid="logo-crop-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-2"><CropIcon className="w-5 h-5 text-[#FF5A00]"/> Crop your logo</DialogTitle></DialogHeader>
          <div className="relative bg-zinc-900 rounded-lg overflow-hidden" style={{ height: 380 }}>
            {pickedSrc && (
              <Cropper
                image={pickedSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect || undefined}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={true}
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Aspect ratio</div>
              <div className="inline-flex bg-zinc-100 rounded-md p-0.5 gap-0.5 w-full">
                {[
                  ["free", "Free", null],
                  ["1", "1:1", 1],
                  ["16-9", "16:9", 16/9],
                  ["4-1", "4:1", 4/1],
                ].map(([k, label, a]) => (
                  <button
                    key={k}
                    onClick={() => setAspect(a)}
                    className={`flex-1 px-2 py-1 text-xs rounded transition ${aspect === a ? "bg-white shadow-sm text-zinc-900 font-medium" : "text-zinc-500 hover:text-zinc-900"}`}
                    data-testid={`logo-aspect-${k}`}
                    type="button"
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Zoom</div>
              <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-full" data-testid="logo-zoom-slider"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickedSrc(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="logo-crop-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Saving…</> : "Save cropped logo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * cropToDataUrl — render the source image, crop with the pixel area produced
 * by react-easy-crop, and downscale the result to a max 400px width to keep
 * the stored data URL small.
 */
async function cropToDataUrl(src, area) {
  const img = await loadImage(src);
  const scale = area.width > 800 ? 800 / area.width : 1;
  const w = Math.round(area.width * scale);
  const h = Math.round(area.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, w, h);
  return canvas.toDataURL("image/png");
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
